import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { orders, type OrderRequest } from "@/api/orders";
import { useActiveAccount } from "@/components/ActiveAccount";
import { useToast } from "@/components/Toast";
import { usePreferences } from "./usePreferences";

interface OneClickInput {
  symbol: string;
  asset_class: string;
  side: "BUY" | "SELL";
  order_type?: "MARKET" | "LIMIT";
  /** Limit price when order_type='LIMIT'; ignored otherwise. */
  price?: number;
  /** Override the default qty pulled from preferences. */
  qty?: number;
}

/**
 * Fire-and-toast order submission for one-click trading from the
 * order book or chart. Pulls the default qty + confirm-on-click
 * setting from user preferences:
 *
 *   prefs.oneclick_default_qty (string, default "1")
 *   prefs.oneclick_confirm     (boolean, default true)
 *
 * Submits via the same /api/orders surface the OrderTicketPane uses,
 * so routing / margin / fills go through the routing engine when an
 * active trading_account_id is set.
 */
export function useOneClickOrder() {
  const qc = useQueryClient();
  const toast = useToast();
  const { active } = useActiveAccount();
  const prefs = usePreferences();

  const data = prefs.data as Record<string, unknown>;
  const defaultQty = (data.oneclick_default_qty as string | undefined) ?? "1";
  const confirmFirst = data.oneclick_confirm !== false; // default true

  const submit = useMutation({
    mutationFn: async (body: OrderRequest & { trading_account_id?: string }) =>
      orders.create(body),
    onSuccess: (r) => {
      toast.push(`${r.message}`, "success");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
      qc.invalidateQueries({ queryKey: ["margin"] });
    },
    onError: (e) =>
      toast.push(
        e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e),
        "error",
      ),
  });

  const fire = useCallback(
    (input: OneClickInput): boolean => {
      const qty = input.qty ?? Number(defaultQty);
      if (!qty || qty <= 0) {
        toast.push("Invalid one-click quantity", "error");
        return false;
      }
      const orderType = input.order_type ?? "MARKET";
      const isLimit = orderType === "LIMIT";
      if (isLimit && (!input.price || input.price <= 0)) {
        toast.push("Limit order requires a price", "error");
        return false;
      }
      if (confirmFirst) {
        const verb = input.side === "BUY" ? "Buy" : "Sell";
        const at = isLimit ? `@ ${input.price}` : "at market";
        const msg = `${verb} ${qty} ${input.symbol} ${at}?`;
        if (!window.confirm(msg)) return false;
      }
      submit.mutate({
        symbol: input.symbol,
        asset_class: input.asset_class,
        side: input.side,
        order_type: orderType,
        quantity: qty,
        limit_price: isLimit ? input.price : null,
        strategy_id: "oneclick",
        trading_account_id: active?.id,
      });
      return true;
    },
    [active?.id, confirmFirst, defaultQty, submit, toast],
  );

  return {
    fire,
    pending: submit.isPending,
    /** Lets callers render the "1-click" badge when confirm is OFF. */
    confirmOn: confirmFirst,
    defaultQty,
  };
}
